import { useRef, useCallback } from 'react';

/**
 * Hook that detects horizontal and vertical swipe gestures.
 * Returns touch handlers to attach to a container element.
 *
 * onSwipeLeft / onSwipeRight: navigate between days
 * onSwipeUp / onSwipeDown: navigate between job/task combos
 */
export default function useSwipe({ onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown }) {
  const touchStart = useRef({ x: 0, y: 0 });
  const touchEnd = useRef({ x: 0, y: 0 });

  const MIN_SWIPE = 50; // minimum px to count as a swipe

  const onTouchStart = useCallback((e) => {
    const touch = e.targetTouches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    touchEnd.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const onTouchMove = useCallback((e) => {
    const touch = e.targetTouches[0];
    touchEnd.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const onTouchEnd = useCallback(() => {
    const dx = touchStart.current.x - touchEnd.current.x;
    const dy = touchStart.current.y - touchEnd.current.y;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Determine dominant direction
    if (absDx > absDy && absDx > MIN_SWIPE) {
      if (dx > 0) onSwipeLeft?.();
      else onSwipeRight?.();
    } else if (absDy > absDx && absDy > MIN_SWIPE) {
      if (dy > 0) onSwipeUp?.();
      else onSwipeDown?.();
    }
  }, [onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}
